import { Link } from "react-router-dom";
import type { Drama } from "@/types/drama";

interface DetailHeroProps {
  drama: Drama;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onPlay: () => void;
}

export default function DetailHero({ drama, isFavorite, onToggleFavorite, onPlay }: DetailHeroProps) {
  return (
    <section className="relative overflow-hidden bg-primary-950">
      <div className="absolute inset-0">
        <img
          src={drama.banner}
          alt={`${drama.title} 剧照`}
          title={`${drama.title} 高清电视剧在线观看`}
          className="h-full w-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-transparent to-primary-950/30" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1280px] px-4 pb-10 pt-24 md:px-8 md:pb-12 md:pt-28">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-background-400">
          <Link to="/" className="transition-colors hover:text-accent-400">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
          <Link to="/category" className="transition-colors hover:text-accent-400">
            电视剧
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
          <span className="text-background-200">{drama.title}</span>
        </nav>

        <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
          <div className="w-[150px] shrink-0 self-start overflow-hidden rounded-lg border border-background-50/15 md:w-[220px]">
            <img
              src={drama.cover}
              alt={`${drama.title} 海报`}
              title={`${drama.title} 高清电视剧在线观看`}
              className="aspect-[5/7] w-full object-cover object-top"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-semibold text-primary-950">
                <i className="ri-fire-line" aria-hidden="true" />
                {drama.status}
              </span>
              <span className="rounded-full border border-background-50/25 px-2.5 py-1 text-[11px] text-background-200">
                {drama.year}
              </span>
              <span className="rounded-full border border-background-50/25 px-2.5 py-1 text-[11px] text-background-200">
                {drama.region}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-3xl font-black leading-tight text-background-50 md:text-[40px]">
              {drama.title}
            </h1>

            <p className="mt-2 text-sm text-background-300">
              {drama.genres.join(" · ")} · 共 {drama.totalEpisodes} 集 · 导演 {drama.director}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <span className="flex items-baseline gap-1 text-accent-400">
                <span className="font-heading text-2xl font-bold">{drama.rating.toFixed(1)}</span>
                <span className="text-xs text-background-300">分</span>
              </span>
              <span className="flex items-center gap-1.5 text-xs text-background-300">
                <i className="ri-eye-line text-base" aria-hidden="true" />
                {drama.views} 次观看
              </span>
              <span className="flex items-center gap-1.5 text-xs text-background-300">
                <i className="ri-user-star-line text-base" aria-hidden="true" />
                主演 {drama.cast.join("、")}
              </span>
            </div>

            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-300">
              {drama.synopsis}
            </p>

            <div className="mt-5 flex flex-wrap gap-1.5">
              {drama.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-50/20 px-2.5 py-1 text-[11px] text-background-300"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={onPlay}
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill text-base" aria-hidden="true" />
                立即观看（演示）
              </button>
              <button
                type="button"
                onClick={onToggleFavorite}
                aria-pressed={isFavorite}
                className={`inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border px-6 py-3 text-sm font-medium transition-colors ${
                  isFavorite
                    ? "border-accent-500 bg-accent-500/15 text-accent-300"
                    : "border-background-50/30 text-background-50 hover:border-background-50/70 hover:bg-background-50/10"
                }`}
              >
                <i className={isFavorite ? "ri-heart-fill" : "ri-heart-line"} aria-hidden="true" />
                {isFavorite ? "已收藏" : "收藏剧集"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}